// components/auth/LoginForm.jsx
'use client';

import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';

import { logger } from '@/utils/log';
export default function LoginForm({ onSuccess, onPasswordReset, onCreateAccount }) {
    const { login } = useAuth();
    // These were pre-filled with a real super-admin account and its password
    // during development. That shipped the credential in the client bundle to
    // anyone who opened the login page, so the fields now start empty.
    const [formData, setFormData] = useState({
        username: '',
        password: ''
    });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const result = await login(formData);

            if (result.success) {
                if (result.twoFactorRequired) {
                    onSuccess({
                        twoFactorRequired: true,
                        twoFactorMethod: result.twoFactorMethod,
                        availableMethods: result.availableMethods
                    });
                } else {
                    onSuccess({ success: true });
                }
            } else {
                setError(result.error || 'Login failed');
            }
        } catch (err) {
            setError('An unexpected error occurred');
            // Detail goes to the console, not to the sign-in screen: this used to
            // render `Debug: Exception: <raw JS message>` to whoever was logging in.
            logger.error('Login error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user types
        if (error) setError('');
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            {/* Username */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Username
                </label>
                <input
                    type="text"
                    name="username"
                    placeholder="Username"
                    value={formData.username}
                    onChange={handleChange}
                    className="input-field"
                    required
                    disabled={loading}
                />
            </div>

            {/* Password */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password
                </label>
                <input
                    type="password"
                    name="password"
                    placeholder="Password"
                    value={formData.password}
                    onChange={handleChange}
                    className="input-field"
                    required
                    disabled={loading}
                />
            </div>

            {/* Error message */}
            {error && (
                <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {error}
                </div>
            )}

            {/* Submit button */}
            <button
                type="submit"
                disabled={loading}
                className="btn btn-primary w-full"
            >
                {loading ? 'Signing in...' : 'Sign in'}
            </button>
        </form>
    );
}